<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="close">
      <div class="howto" role="dialog" aria-label="How to play">
        <button class="howto__close" aria-label="Close" @click="close">✕</button>

        <div class="howto__body">
          <h3>{{ slide.title }}</h3>

          <div v-if="slide.cards" class="howto__cards">
            <img
              v-for="id in slide.cards"
              :key="id"
              :src="`${base}card-art/${id}.jpg`"
              :alt="altFor(id)"
              class="howto__card"
            >
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -- content is this file's own
               constant strings below, never user input -->
          <p v-for="(line, i) in slide.lines" :key="i" class="howto__line" v-html="line" />
        </div>

        <div class="howto__nav">
          <button class="secondary" :disabled="index === 0" @click="index -= 1">Back</button>
          <span class="howto__dots">
            <i
              v-for="(s, i) in slides"
              :key="i"
              class="howto__dot"
              :class="{ 'howto__dot--on': i === index }"
            />
          </span>
          <button v-if="index < slides.length - 1" class="primary howto__next" @click="index += 1">
            Next
          </button>
          <button v-else class="primary howto__next" @click="close">
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { cardById } from '@/engine/deck';

// The walkthrough. Matt: friends he invites have often never played, and they
// should be able to "get a sense of the lay of the land when they first
// arrive" without waiting for him to teach it.
//
// Slides, not a rules document - each one is a single idea with the game's own
// card art doing most of the talking. The full nuances (artifact values,
// remainder handling) are deliberately left out: this teaches enough to take
// your first turn without being lost, and the interface itself narrates the
// details as they come up ("bank 4, share up to 7" on the button, the verdict
// line between rounds).
//
// App.vue owns WHEN this shows: automatically on a first-ever visit, and from
// the header's ? button forever after.
const SLIDES = [
  {
    title: 'Welcome to the temple',
    cards: ['t8', 't13'],
    lines: [
      'You and your fellow explorers are raiding an old temple, <strong>together</strong> — five expeditions in.',
      'Cards turn over one at a time. <strong>Treasure</strong> is split evenly between everyone still inside, and the leftovers stay lying on the path.',
      'Whoever banks the most treasure by the end wins.',
    ],
  },
  {
    title: 'The one decision',
    lines: [
      'After each card, everyone secretly chooses at the same time:',
      '<strong>Keep going</strong> — deeper in, richer splits, more danger.',
      '<strong>Head back</strong> — bank everything you\'re carrying, safely. You also sweep up whatever treasure is lying on the path — split with anyone who leaves with you.',
      'Once you\'re out, you\'re out until the next expedition.',
    ],
  },
  {
    title: 'The hazards',
    cards: ['snake0', 'spider0', 'lava0', 'boulder0', 'spikes0'],
    lines: [
      'Five kinds of danger hide in the deck. The <strong>first</strong> of a kind is just a warning.',
      'The <strong>second of the same kind</strong> ends the expedition on the spot — everyone still inside drops everything they picked up this round and runs.',
      'The little pictures at the top of the screen show which hazards are already out. The more that are lit, the more dangerous the next card is.',
    ],
  },
  {
    title: 'Artifacts',
    cards: ['a0'],
    lines: [
      'Sometimes an <strong>artifact</strong> turns up. It sits on the path, worth a lot of points.',
      'It goes to the next player who heads back <strong>alone</strong>. Leave together and nobody gets it.',
      'If the expedition ends while it\'s still lying there, it\'s lost for good.',
    ],
  },
  {
    title: 'Reading the table',
    lines: [
      'Everyone\'s <strong>pocket</strong> — what they\'re carrying this round — is public. Watch it grow, and guess who\'s about to bolt.',
      'Everyone\'s <strong>tent</strong> — what they\'ve banked — is secret until the end. That\'s the <strong>?</strong> next to each name.',
      'That\'s the whole game: be greedy exactly one card longer than it\'s safe to be. Good luck.',
    ],
  },
];

export default {
  name: 'HowToPlay',
  emits: ['close'],
  data() {
    return { index: 0, slides: SLIDES, base: process.env.BASE_URL || '/' };
  },
  computed: {
    slide() { return this.slides[this.index]; },
  },
  methods: {
    close() { this.$emit('close'); },
    altFor(id) {
      const card = cardById(id);
      if (!card) return '';
      if (card.kind === 'treasure') return `A treasure card worth ${card.value}`;
      if (card.kind === 'artifact') return 'An artifact card';
      return `A ${card.hazard} hazard card`;
    },
  },
};
</script>
